/**
 * Shared Email Template Execution
 *
 * Single render path for both test-send (from template editor) and
 * Rules Engine production email delivery. Handles:
 *   - Loading template ActivityDefinition and extracting config
 *   - Validating template status
 *   - Resolving runtime variables (built-in catalog + custom)
 *   - Rendering subject/htmlBody/textBody
 *   - Checking missing required variables
 *   - Deriving/stamping preference category
 *   - HTML sanitization of dynamic values
 *
 * Rules Engine executeEmailIntent and notification-runtime sendTestEmail
 * both call through this shared service. Neither creates a Task (ENG-3764 D24):
 * the test send calls the `email-sender` bot's `send-test-email` action,
 * which sends to the requester's own email with a `[TEST]` subject.
 */
import type { MedplumClient } from '@medplum/core';
import type { ActivityDefinition } from '@medplum/fhirtypes';

import type {
  NotificationTemplateConfig,
  NotificationTemplateStatus,
} from '../notification-template.types';
import type { NotificationEventContext } from '../notification-automation.types';
import {
  NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  NOTIFICATION_AD_URL_PREFIX,
} from '../notification-template.constants';
import { getJsonExtension } from '../notification-template.medplum';
import { buildClientPortalScheduleUrl, buildClientSignInUrl } from '@/lib/clientPortalRoutes';
import { resolvePublicAppBaseUrl } from '@/lib/publicAppBaseUrl';
import { renderTemplateString } from './notification-template-renderer';
import { resolveEmailBranding } from '@unify/fhir-model/email-layout';
import { wrapEmailHtml } from './notification-email-html';
import {
  resolveAllVariableValues,
  buildTestOrPreviewValueMap,
  type ResourceCache,
} from './notification-variable-resolver';
import { escapeHtmlValue } from './notification-html-sanitizer';


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LoadedTemplateConfig {
  config: NotificationTemplateConfig;
  activityDefinition: ActivityDefinition;
  fhirStatus: string;
  templateStatus: NotificationTemplateStatus;
}

export interface RenderResult {
  subject: string;
  htmlBody: string;
  textBody: string;
  unresolvedRequired: string[];
}

export interface RenderForEventOptions {
  mode: 'production' | 'test';
  eventContext?: NotificationEventContext;
  sampleContext?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// 1. Load template config
// ---------------------------------------------------------------------------

export async function loadNotificationTemplateConfig(
  medplum: MedplumClient,
  templateId: string,
): Promise<LoadedTemplateConfig | null> {
  let ad: ActivityDefinition | undefined;
  try {
    ad = await medplum.readResource('ActivityDefinition', templateId);
  } catch {
    ad = undefined;
  }

  if (!ad) {
    const canonicalUrl = templateId.startsWith('http')
      ? templateId
      : `${NOTIFICATION_AD_URL_PREFIX}${templateId}`;
    try {
      ad = (await medplum.searchOne('ActivityDefinition', { url: canonicalUrl })) ?? undefined;
    } catch {
      ad = undefined;
    }
  }

  if (!ad) return null;

  const config = getJsonExtension<NotificationTemplateConfig>(
    ad,
    NOTIFICATION_TEMPLATE_CONFIG_EXTENSION,
  );
  if (!config) return null;

  const templateStatus: NotificationTemplateStatus = config.status ?? 'draft';

  return {
    config: { ...config, id: ad.id ?? config.id },
    activityDefinition: ad,
    fhirStatus: ad.status ?? 'draft',
    templateStatus,
  };
}

// ---------------------------------------------------------------------------
// 2. Validate template status for runtime use
// ---------------------------------------------------------------------------

export type TemplateStatusValidation =
  | { valid: true }
  | { valid: false; reason: string };

export function validateTemplateStatusForMode(
  loaded: LoadedTemplateConfig,
  mode: 'production' | 'test',
): TemplateStatusValidation {
  if (mode === 'test') {
    if (loaded.templateStatus === 'archived') {
      return { valid: false, reason: 'template-archived' };
    }
    return { valid: true };
  }

  if (loaded.templateStatus !== 'active') {
    return {
      valid: false,
      reason: loaded.templateStatus === 'draft'
        ? 'template-draft'
        : loaded.templateStatus === 'archived'
          ? 'template-archived'
          : 'template-invalid-status',
    };
  }
  return { valid: true };
}

// ---------------------------------------------------------------------------
// 3. Render template for an event or test
// ---------------------------------------------------------------------------

export async function renderNotificationTemplate(
  medplum: MedplumClient,
  config: NotificationTemplateConfig,
  options: RenderForEventOptions,
): Promise<RenderResult> {
  let values: Record<string, string>;
  // Shared so the Organization is read at most once per render (variables + branding).
  const resourceCache: ResourceCache = {};

  if (options.eventContext) {
    values = await resolveAllVariableValues(config, options.eventContext, medplum, resourceCache);
    if (options.sampleContext) {
      for (const [k, v] of Object.entries(options.sampleContext)) {
        if (!values[k]) values[k] = v;
      }
    }
  } else {
    values = buildTestOrPreviewValueMap(
      { subject: config.subject, htmlBody: config.htmlBody, textBody: config.textBody, variables: config.variables },
      options.sampleContext,
    );
    const base = await resolvePublicAppBaseUrl(medplum);
    if (!options.sampleContext?.['portal.link']) {
      values['portal.link'] = buildClientSignInUrl(base);
    }
    if (!options.sampleContext?.['practice.scheduleLink']) {
      values['practice.scheduleLink'] = buildClientPortalScheduleUrl(base);
    }
  }

  const escapedValues = escapeVariableValuesForHtml(values);

  const subjectResult = renderTemplateString(config.subject, config.variables, values);
  const htmlBodyResult = renderTemplateString(config.htmlBody, config.variables, escapedValues);
  const textBodyResult = renderTemplateString(config.textBody ?? '', config.variables, values);

  const unresolvedRequired = [
    ...new Set([
      ...subjectResult.unresolvedRequired,
      ...htmlBodyResult.unresolvedRequired,
      ...textBodyResult.unresolvedRequired,
    ]),
  ];

  const branding = resolveEmailBranding();

  return {
    subject: subjectResult.rendered,
    // Template files are body fragments; wrapEmailHtml sanitizes, then applies the shared layout.
    htmlBody: wrapEmailHtml(htmlBodyResult.rendered, branding),
    textBody: textBodyResult.rendered,
    unresolvedRequired,
  };
}

function escapeVariableValuesForHtml(values: Record<string, string>): Record<string, string> {
  const escaped: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    escaped[k] = escapeHtmlValue(v);
  }
  return escaped;
}
